import { Component, type ErrorInfo, type ReactNode } from 'react';

import styles from './ErrorBoundary.module.css';

interface State {
  error: Error | null;
}

/**
 * Turns a render crash into something a customer can act on.
 *
 * Without one of these React unmounts the whole tree on any thrown error, and
 * the Mini App becomes a blank white screen with no way out but force-quitting
 * Telegram. That is indistinguishable from the app being broken, and it hides
 * the error from us as well as from them.
 *
 * Deliberately a class: an error boundary is the one thing hooks still cannot
 * express.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console rather than sent anywhere: this runs on a customer's
    // phone, and the message can carry whatever was on screen when it broke.
    console.error('[yeneshop] render failed', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className={styles.wrap} role="alert">
        <p className={styles.emoji}>😞</p>
        <p className={styles.title}>Something went wrong</p>
        <p className={styles.body}>
          The app hit an unexpected error. Reloading usually fixes it — your balance and orders
          are safe.
        </p>
        <button
          type="button"
          className={styles.button}
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
}
